import * as TabsPrimitive from "@radix-ui/react-tabs";
import type { ReactNode } from "react";

import { cn } from "../lib/cn";
import { FOCUS_RING, REDUCED_MOTION_SAFE } from "../lib/focus";
import { omitUndefined } from "../lib/optional";

export type typeTabItem = {
  value: string;
  label: string;
  /** A count, shown as a pill after the label — "Active 3". */
  badge?: number | undefined;
  /**
   * A decorative glyph drawn above the label below 640px only. It is wrapped
   * in an aria-hidden span, so the label stays the tab's whole accessible name.
   */
  icon?: ReactNode | undefined;
  content: ReactNode;
  disabled?: boolean | undefined;
};

export type typeTabsProps = {
  /** Names the tab list for screen readers. Not rendered visually. */
  label: string;
  items: readonly typeTabItem[];
  /**
   * Controlled active tab.
   *
   * The active tab belongs in a query param (`?tab=details`),
   * not in component state — otherwise the browser back button leaves the
   * screen instead of returning to the previous tab, and a shared link opens on
   * the wrong one.
   */
  value?: string | undefined;
  defaultValue?: string | undefined;
  onValueChange?: ((value: string) => void) | undefined;
  className?: string | undefined;
  /**
   * Keeps every tab's content mounted, so the tab a fan leaves still holds
   * what they typed when they come back. Off by default: a tab set whose
   * panels are cheap reads has no reason to pay for four mounted subtrees.
   *
   * Radix hides a force-mounted panel with the `hidden` attribute. A Tailwind
   * `display` utility on a child can beat that attribute, so the panel also
   * carries `data-[state=inactive]:hidden` and the two agree.
   */
  keepMounted?: boolean | undefined;
  /**
   * Below 640px, spread the triggers across the row in equal columns. Off by
   * default: a long tab row keeps scrolling inside the list. Every class it
   * adds is a `max-sm:` utility, so 640px and up are untouched.
   */
  fillOnPhone?: boolean | undefined;
  /**
   * `detail` is the underline tab row of a game, team or player page (Scores
   * UX spec v1.0 section 7.2): 48px triggers with 16px of padding each and a
   * `caption` 13.5/600 label, start aligned and scrolling sideways. `default`
   * keeps the 16px body label every other screen uses.
   */
  variant?: "default" | "detail" | undefined;
  /** Extra classes on the tab list, for a screen that pins it (`sticky`). */
  listClassName?: string | undefined;
};

/**
 * A tab set on Radix, which owns the ARIA tabs keyboard contract: one tab stop
 * for the whole list, arrow keys to move, Home/End to jump.
 *
 * The active tab is marked with a 2px solid `primary` underline with a
 * rounded top, plus brand-colored, bolded text (theme.mdc), never a pale tint
 * fill alone, which reads as "maybe selected" at the glance this audience
 * gives it.
 *
 * `icon` and `fillOnPhone` are opt in and phone only (ISSUE-965): the icon
 * sits above the label and the triggers share the row equally under 640px,
 * and nothing changes at 640px and up. They use `max-sm:` utilities, which
 * cannot collide with an `md:` or `lg:` value on the same property.
 */
export const Tabs = ({
  label,
  items,
  value,
  defaultValue,
  onValueChange,
  className,
  keepMounted = false,
  fillOnPhone = false,
  variant = "default",
  listClassName,
}: typeTabsProps): ReactNode => (
  <TabsPrimitive.Root
    className={cn("flex w-full flex-col gap-4", className)}
    {...omitUndefined({
      value,
      defaultValue: defaultValue ?? items[0]?.value,
      onValueChange,
    })}
  >
    <TabsPrimitive.List
      aria-label={label}
      className={cn(
        "flex gap-1 border-b border-hairline",
        // A long tab row scrolls sideways INSIDE the list rather than making
        // the page scroll horizontally.
        "overflow-x-auto",
        fillOnPhone ? "max-sm:gap-0" : "",
        variant === "detail" ? "gap-0 bg-bg" : "",
        listClassName,
      )}
    >
      {items.map((item) => (
        <TabsPrimitive.Trigger
          key={item.value}
          value={item.value}
          disabled={item.disabled ?? false}
          className={cn(
            "relative flex shrink-0 items-center gap-2",
            variant === "detail"
              ? "min-h-control-height px-4 text-caption text-ink-secondary"
              : "min-h-touch-target px-3 pb-2 text-body text-ink-secondary",
            "transition-colors duration-100 ease-standard",
            REDUCED_MOTION_SAFE,
            FOCUS_RING,
            "hover:text-ink",
            // The default row marks the active tab with weight too. The detail
            // row keeps the caption's 600 in both states (spec 7.2), so the
            // selected label never widens and shifts the tabs sideways.
            variant === "detail" ? "" : "data-[state=active]:font-bold",
            "data-[state=active]:text-primary",
            "data-[state=active]:after:absolute data-[state=active]:after:inset-x-0",
            "data-[state=active]:after:bottom-0 data-[state=active]:after:h-0.5 data-[state=active]:after:rounded-t-sm",
            "data-[state=active]:after:bg-primary data-[state=active]:after:content-['']",
            "disabled:cursor-not-allowed disabled:text-disabled-text",
            fillOnPhone
              ? "max-sm:min-w-0 max-sm:flex-1 max-sm:flex-col max-sm:justify-center max-sm:gap-1 max-sm:px-1 max-sm:pt-2"
              : "",
          )}
        >
          {item.icon !== undefined ? (
            <span
              aria-hidden="true"
              data-tab-icon=""
              className="hidden max-sm:flex"
            >
              {item.icon}
            </span>
          ) : null}
          {item.icon !== undefined ? (
            <span data-tab-label="">{item.label}</span>
          ) : (
            item.label
          )}
          {typeof item.badge === "number" ? (
            <span
              className={cn(
                "rounded-full bg-surface-sunken px-2 text-caption text-ink-secondary",
                "data-[state=active]:bg-primary-tint",
              )}
            >
              {item.badge}
            </span>
          ) : null}
        </TabsPrimitive.Trigger>
      ))}
    </TabsPrimitive.List>

    {items.map((item) => (
      <TabsPrimitive.Content
        key={item.value}
        value={item.value}
        className={cn(
          "outline-none",
          FOCUS_RING,
          keepMounted ? "data-[state=inactive]:hidden" : "",
        )}
        {...(keepMounted ? { forceMount: true as const } : {})}
      >
        {item.content}
      </TabsPrimitive.Content>
    ))}
  </TabsPrimitive.Root>
);
